import { ReactElement } from "react";

import { Guid } from "@/Common/Models/Guid";
import { LinkButton } from "@/DesignSystem";
import { useGetFlowsheetName } from "@/Flowsheets/Hooks/useGetFlowsheetName";
import { useSelector } from "@/Flowsheets/Hooks/useSelector";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { IFlowsheetModel } from "@/Patient/Models/JSONInterfaces/Flowsheet/IFlowsheetModel";

import styles from "./FlowsheetsList.module.scss";

/**
 * The properties for a {@link FlowsheetsList} component.
 */
interface IFlowsheetsListProps {
    /**
     * A callback invoked when the flowsheet is clicked.
     * @param flowsheet The clicked flowsheet.
     */
    onClick: (flowsheet: FlowsheetSummary) => void;

    /**
     * Current patient id
     */
    patientId: Guid;

    /**
     * Current encounter id
     */
    encounterId: Guid;
}

/**
 * A horizontal list of flowsheets.
 * @param props The FlowsheetsList properties.
 */
function FlowsheetsList({ onClick, patientId, encounterId }: Readonly<IFlowsheetsListProps>): ReactElement {
    const { getName } = useGetFlowsheetName();
    const flowsheets = useSelector(s => {
        if (s.patient.FlowsheetModels[patientId]) {
            return s.patient.FlowsheetModels[patientId]
                .map((flowsheet: IFlowsheetModel) => flowsheet.get("flowsheetSummary"))
                .filter(p => p != null && // Some summaries may not be available yet as they may be loading
                    p.EncounterId === encounterId
                );
        }

        return [];
    });

    return (
        <ul className={styles.flowsheetList} data-cy="flowsheet-list">
            {flowsheets.map(flowsheet => (
                <li key={`flowsheetlist_${flowsheet.Id}`} className={styles.flowsheetListItem}>
                    <LinkButton
                        label={getName(flowsheet.Template)}
                        onClick={() => onClick(flowsheet)}
                        underline={false}
                    />
                </li>
            ))}
        </ul>
    );
}

export { FlowsheetsList };
